import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ApplyLeave() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me } = useApp();
  const { data: leave } = useQuery({ queryKey: ["leave"], queryFn: () => get("/hrms/leave") });
  const [form, setForm] = useState({ type_id: "", from: "", to: "", days: "", reason: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const apply = useMutation({
    mutationFn: (body) => post("/hrms/leave/apply", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leave"] }); toast.success("Leave application submitted"); navigate("/hrms/leave"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not submit leave application"),
  });

  const submit = () => {
    if (!form.type_id || !form.from || !form.to || !form.days) { toast.error("All fields except reason are required"); return; }
    apply.mutate({ ...form, days: Number(form.days), employee_id: me?.id });
  };

  return (
    <div className="max-w-xl">
      <PageHeader title="Apply for Leave" subtitle="Submit a new leave request for approval." />
      <Section>
        <div className="grid grid-cols-2 gap-4 p-4">
          <div className="col-span-2">
            <Label>Leave Type *</Label>
            <Select value={form.type_id} onValueChange={(v) => set("type_id", v)}>
              <SelectTrigger className="mt-1" data-testid="field-type_id"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{leave?.types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>From *</Label><Input data-testid="field-from" type="date" className="mt-1" value={form.from} onChange={(e) => set("from", e.target.value)} /></div>
          <div><Label>To *</Label><Input data-testid="field-to" type="date" className="mt-1" value={form.to} onChange={(e) => set("to", e.target.value)} /></div>
          <div><Label>Days *</Label><Input data-testid="field-days" type="number" className="mt-1" value={form.days} onChange={(e) => set("days", e.target.value)} /></div>
          <div className="col-span-2"><Label>Reason</Label><Textarea data-testid="field-reason" className="mt-1" value={form.reason} onChange={(e) => set("reason", e.target.value)} /></div>
        </div>
      </Section>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={() => navigate("/hrms/leave")}>Cancel</Button>
        <Button data-testid="submit-leave" onClick={submit} disabled={apply.isPending}>Submit Request</Button>
      </div>
    </div>
  );
}
